import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { api, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface Note { id: number; title: string; content: string; updatedAt: string }
interface NoteLog { id: number; action: string; details: string; createdAt: string }

const SAVE_DELAY = 1000;
const RETRY_DELAY = 3000;
const CHECKLIST = '<div class="checklist-item"><input type="checkbox"><span>&nbsp;</span></div>';

/**
 * Užrašai as in the PHP panel: one full-height frame with the note list on the left (search, new note),
 * a borderless page-like editor in the middle and the activity log folded on the right. Saves itself.
 */
export function NotesPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [liveTitle, setLiveTitle] = useState<{ id: number; title: string } | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const notes = useQuery({ queryKey: ['notes'], queryFn: () => api.get<{ notes: Note[] }>('api/v1/notes').then((r) => r.notes) });
  const create = useMutation({
    mutationFn: () => api.post<{ note: Note }>('api/v1/notes', { title: t('Be pavadinimo'), content: '' }),
    onSuccess: async (r) => { await queryClient.invalidateQueries({ queryKey: ['notes'] }); setOpenId(r.note.id); setListOpen(false); },
  });
  const all = [...(notes.data ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  // The newest note opens by itself.
  const selected = openId !== null && all.some((n) => n.id === openId) ? openId : all[0]?.id ?? null;
  const titleOf = (n: Note) => (liveTitle?.id === n.id ? liveTitle.title : n.title) || t('Be pavadinimo');
  const list = all.filter((n) => !q || titleOf(n).toLowerCase().includes(q.toLowerCase()));
  const open = all.find((n) => n.id === selected) ?? null;
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Darbas')} title={t('Užrašai')} text={t('Užrašai, kontroliniai sąrašai ir veiklos istorija.')} />}>
      <section className="notes-frame-panel notes-app">
        <div className={`notes-wrapper${listOpen ? ' list-open' : ''}`}>
          <aside className="notes-side">
            <div className="notes-search"><input type="search" placeholder={t('Ieškoti užrašų')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('Ieškoti užrašų')} /></div>
            <div className="notes-new"><button type="button" onClick={() => create.mutate()} disabled={create.isPending}>+ {t('Naujas užrašas')}</button></div>
            <nav className="notes-items">
              {list.map((n) => (
                <button key={n.id} type="button" className={`notes-item${n.id === selected ? ' active' : ''}`} onClick={() => { setOpenId(n.id); setListOpen(false); }}>
                  <strong>{titleOf(n)}</strong><small>{new Date(n.updatedAt).toLocaleString()}</small>
                </button>
              ))}
            </nav>
          </aside>
          <button type="button" className="notes-scrim" aria-label={t('Uždaryti')} onClick={() => setListOpen(false)} />
          <main className="notes-stage">
            {open ? <NoteEditor key={open.id} note={open} onTitle={(title) => setLiveTitle({ id: open.id, title })} onDeleted={() => setOpenId(null)} onList={() => setListOpen(true)} />
              : <div className="notes-empty"><span aria-hidden="true">📝</span><p>{t('Pasirinkite arba sukurkite užrašą.')}</p><button type="button" className="notes-list-toggle" onClick={() => setListOpen(true)}>☰ {t('Užrašai')}</button></div>}
          </main>
          {open ? <NoteLogRail noteId={open.id} updatedAt={open.updatedAt} /> : null}
        </div>
      </section>
    </PanelShell>
  );
}

function NoteEditor({ note, onTitle, onDeleted, onList }: { note: Note; onTitle: (title: string) => void; onDeleted: () => void; onList: () => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const editor = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState(note.title);
  const [state, setState] = useState<'saved' | 'dirty' | 'saving' | 'failed'>('saved');
  const timer = useRef<number | undefined>(undefined);
  const dirty = useRef(false);
  const titleRef = useRef(title);
  useEffect(() => { titleRef.current = title; }, [title]);

  const body = () => ({ title: titleRef.current.trim() || t('Be pavadinimo'), content: editor.current?.innerHTML ?? '' });
  const save = async () => {
    window.clearTimeout(timer.current);
    if (!dirty.current) return;
    dirty.current = false;
    setState('saving');
    try {
      await api.put(`api/v1/notes/${note.id}`, body());
      setState(dirty.current ? 'dirty' : 'saved');
      await queryClient.invalidateQueries({ queryKey: ['notes'] });
    } catch {
      // Not saved (connection lost?): keep the changes and try again in a moment.
      dirty.current = true;
      setState('failed');
      timer.current = window.setTimeout(() => void save(), RETRY_DELAY);
    }
  };
  const changed = () => {
    dirty.current = true;
    setState('dirty');
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void save(), SAVE_DELAY);
  };
  const remove = useMutation({ mutationFn: () => api.delete(`api/v1/notes/${note.id}`), onSuccess: async () => { dirty.current = false; await queryClient.invalidateQueries({ queryKey: ['notes'] }); onDeleted(); } });

  // Content comes back from the server already sanitized (the same allow-list the editor produces).
  useEffect(() => { if (editor.current) editor.current.innerHTML = note.content; }, [note.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Leaving the page or the note with unsaved changes: send them anyway (keepalive outlives the page).
  useEffect(() => {
    const flush = () => {
      if (!dirty.current) return;
      dirty.current = false;
      void fetch(`api/v1/notes/${note.id}`, { method: 'PUT', keepalive: true, credentials: 'same-origin', headers: { 'content-type': 'application/json', 'x-fsp-request': '1' }, body: JSON.stringify(body()) });
    };
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => { document.removeEventListener('visibilitychange', onHide); window.removeEventListener('pagehide', flush); window.clearTimeout(timer.current); flush(); };
  }, [note.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addChecklist = () => {
    editor.current?.focus();
    document.execCommand('insertHTML', false, CHECKLIST);
    changed();
  };
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const box = event.target as HTMLElement;
    if (box instanceof HTMLInputElement && box.type === 'checkbox') {
      if (box.checked) box.setAttribute('checked', 'checked'); else box.removeAttribute('checked');
      box.nextElementSibling?.classList.toggle('checklist-done', box.checked);
      changed();
    }
  };
  // Enter inside a checklist line starts the next checklist line (Enter on an empty one ends the list).
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    const node = window.getSelection()?.anchorNode;
    const item = (node instanceof Element ? node : node?.parentElement)?.closest('.checklist-item');
    if (!item || !editor.current?.contains(item)) return;
    event.preventDefault();
    const empty = !item.textContent?.replace(/ /g, '').trim();
    let target: Node;
    if (empty) {
      const line = document.createElement('div');
      line.innerHTML = '<br>';
      item.replaceWith(line);
      target = line;
    } else {
      const holder = document.createElement('div');
      holder.innerHTML = CHECKLIST;
      const next = holder.firstElementChild!;
      item.after(next);
      target = next.querySelector('span')!;
    }
    const range = document.createRange();
    range.selectNodeContents(target);
    range.collapse(true);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
    changed();
  };

  const status = state === 'saved' ? t('Išsaugota') : state === 'saving' ? t('Išsaugoma…') : state === 'failed' ? t('Išsaugoti nepavyko — bandoma iš naujo') : t('Neišsaugoti pakeitimai');
  return (
    <>
      <div className="notes-toolbar">
        <button type="button" className="notes-list-toggle" onClick={onList}>☰ {t('Užrašai')}</button>
        <span>{t('Paskutinį kartą redaguota')}: {new Date(note.updatedAt).toLocaleString()}</span>
        <div className="notes-toolbar-actions">
          <button type="button" title={t('Kontrolinis sąrašas')} aria-label={t('Kontrolinis sąrašas')} onClick={addChecklist}>☑</button>
          <button type="button" title={t('Pašalinti užrašą')} aria-label={t('Pašalinti užrašą')} onClick={() => { if (confirm(t('Pašalinti užrašą?'))) remove.mutate(); }}>🗑</button>
        </div>
      </div>
      <div className="notes-editor">
        {remove.isError ? <Alert kind="error">{errorText(t, remove.error)}</Alert> : null}
        <input className="notes-title" value={title} maxLength={200} placeholder={t('Be pavadinimo')} aria-label={t('Pavadinimas')}
          onChange={(e) => { setTitle(e.target.value); onTitle(e.target.value); changed(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); editor.current?.focus(); } }} />
        <div ref={editor} className="notes-body" contentEditable suppressContentEditableWarning onInput={changed} onClick={onClick} onKeyDown={onKeyDown} onBlur={() => void save()} />
        <div className={`notes-status${state === 'failed' ? ' is-error' : ''}`} role="status">{status}</div>
      </div>
    </>
  );
}

/** Veiklos žurnalas: the note's history, folded to a thin strip on the right. */
function NoteLogRail({ noteId, updatedAt }: { noteId: number; updatedAt: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const logs = useQuery({ queryKey: ['note-logs', noteId, updatedAt], enabled: open, queryFn: () => api.get<{ logs: NoteLog[] }>(`api/v1/notes/${noteId}/logs`).then((r) => r.logs) });
  return (
    <details className="notes-log" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary><span>{t('Veiklos žurnalas')}</span></summary>
      <div className="notes-log-list">
        {logs.data?.slice(0, 50).map((l) => (
          <div key={l.id} className="notes-log-item"><small>{new Date(l.createdAt).toLocaleString()}</small><div>{l.action}{l.details ? `: ${l.details}` : ''}</div></div>
        ))}
        {logs.data && !logs.data.length ? <p className="muted">{t('Įrašų dar nėra.')}</p> : null}
      </div>
    </details>
  );
}
